import { queryKeys, useHttpClient, useActiveOrganizationId } from '@repo/api-client/react';
import {
  InvoiceSchema,
  type CreateInvoiceRequest,
} from '@repo/api-client/dtos';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { postJSON } from '../internal/http';

/**
 * Create an invoice under the active org. Includes a fresh
 * `Idempotency-Key` so a flaky retry doesn't double-bill the
 * client — the server caches the response under the key for 24h.
 */
export function useCreateInvoice() {
  const http = useHttpClient();
  const queryClient = useQueryClient();
  const orgId = useActiveOrganizationId();

  const mutation = useMutation({
    mutationFn: (body: CreateInvoiceRequest) => {
      if (!orgId) throw new Error('Organization is required to create an invoice');
      return postJSON(http, `/orgs/${orgId}/invoices`, body, InvoiceSchema);
    },
    onSuccess: (invoice) => {
      queryClient.setQueryData(
        queryKeys.invoices.detail(invoice.orgId, invoice.id),
        invoice,
      );
      queryClient.invalidateQueries({
        queryKey: queryKeys.invoices.all(invoice.orgId),
      });
    },
  });

  return {
    createInvoice: mutation.mutateAsync,
    loading: mutation.isPending,
    error: mutation.error,
  };
}
